import {
  ATTACK,
  destinationsFromValues,
} from "./attack-mapping";
import type {
  AttackTechnique,
  ClassInfo,
  DataFlowTrace,
  DetectedAction,
} from "./types";
import type { ThreatContext } from "./threat-intel";

const severityOrder: Record<DetectedAction["severity"], number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  info: 4,
};

function matches(values: string[], patterns: RegExp[]) {
  return values.some((value) =>
    patterns.some((pattern) => {
      pattern.lastIndex = 0;
      return pattern.test(value);
    }),
  );
}

function evidence(values: string[], patterns: RegExp[], limit = 5) {
  const hits: string[] = [];
  for (const value of values) {
    if (
      patterns.some((pattern) => {
        pattern.lastIndex = 0;
        return pattern.test(value);
      })
    ) {
      const clipped =
        value.length > 220 ? `${value.slice(0, 217).trim()}…` : value.trim();
      if (clipped && !hits.includes(clipped)) hits.push(clipped);
    }
    if (hits.length >= limit) break;
  }
  return hits;
}

export function detectActions(
  classes: ClassInfo[],
  contexts: ThreatContext[],
  analysis: {
    flows?: DataFlowTrace[];
    reachableMethodKeys?: string[];
    entryPointLabels?: Record<string, string>;
  } = {},
): DetectedAction[] {
  const actions: DetectedAction[] = [];
  const seen = new Set<string>();
  const seenAtEntry = new Set<string>();
  let id = 0;

  const add = (
    action: Omit<DetectedAction, "id">,
  ) => {
    const key = `${action.actionId}:${action.entry}:${action.method ?? ""}`;
    if (seen.has(key) || actions.length >= 1_000) return;
    seen.add(key);
    seenAtEntry.add(`${action.actionId}:${action.entry}`);
    actions.push({ ...action, id: `action-${++id}` });
  };

  // Bare identifiers like getUsername or accessToken are everywhere in ordinary
  // Java. Calling one a Minecraft account field is only defensible when the
  // archive touches Minecraft at all, so the loose identity heuristics below
  // stay switched off otherwise. Named markers such as mcUsername and the
  // Session/GameProfile call shapes are specific enough to stand alone.
  const minecraftContext = classes.some((classInfo) =>
    [
      classInfo.name,
      classInfo.superClass,
      ...classInfo.interfaces,
      ...classInfo.referencedClasses,
    ].some((reference) =>
      /(?:^|[./])(?:net[./]minecraft|com[./]mojang|net[./]fabricmc|org[./]bukkit|org[./]spongepowered|com[./]velocitypowered|cpw[./]mods|net[./]minecraftforge|net[./]neoforged)\b/i.test(
        reference,
      ),
    ),
  );

  const sessionPatterns = [
    /MinecraftClient\.getSession/i,
    /Minecraft\.getSession/i,
    /MinecraftClient\.getInstance/i,
    /net[./]minecraft[./]class_310\.method_1551/i,
    /net[./]minecraft[./]class_310\.method_1548/i,
  ];
  const usernamePatterns = [
    /Session\.getUsername/i,
    /Session\.getName/i,
    /GameProfile\.getName/i,
    /net[./]minecraft[./]class_320\.method_1674/i,
    /\bmcUsername\b/i,
  ];
  const uuidPatterns = [
    /Session\.getUuid(?:OrNull)?/i,
    /Session\.getProfileId/i,
    /GameProfile\.getId/i,
    /net[./]minecraft[./]class_320\.method_44717/i,
    /\bmcUuid\b/i,
  ];
  const tokenPatterns = [
    /Session\.getAccessToken/i,
    /net[./]minecraft[./]class_320\.method_1676/i,
    /\bgetAccessToken\b/i,
    /\baccess[_ .-]?token\b/i,
  ];
  const serverPatterns = [
    /MinecraftClient\.getCurrentServerEntry/i,
    /Minecraft\.getCurrentServer/i,
    /ServerInfo\.(?:address|field_3761)/i,
    /ServerData\.ip/i,
    /\bserverAddress\b/i,
  ];
  const payPatterns = [/(?:^|[\s"'`])\/pay(?:\s|$)/i];
  const sendChatPatterns = [
    /\.sendChatCommand/i,
    /\.sendChatMessage/i,
    /\.sendCommand/i,
    /\.chat\(/i,
  ];
  const networkPatterns = [
    /java\/net\/URL\.(?:openConnection|openStream)/i,
    /java\/net\/HttpURLConnection/i,
    /java\/net\/http\/HttpClient\.(?:send|sendAsync)/i,
    /okhttp3\/Call\.execute/i,
    /okhttp3\/OkHttpClient\.newCall/i,
    /java\/net\/Socket\./i,
    /org\/apache\/http\/client/i,
  ];
  const remoteReadPatterns = [
    /java\/net\/URL\.openStream/i,
    /InputStream\.(?:read|readAllBytes|transferTo)/i,
    /BodyHandlers\.ofByteArray/i,
    /ResponseBody\.(?:bytes|byteStream)/i,
    /Files\.copy.*InputStream/i,
  ];
  const fileWritePatterns = [
    /java\/nio\/file\/Files\.(?:write|writeString|copy|move)/i,
    /java\/nio\/file\/Files\.newOutputStream/i,
    /FileOutputStream\.(?:<init>|write)/i,
    /FileWriter\.(?:<init>|write)/i,
    /RandomAccessFile\.(?:write|setLength)/i,
  ];
  const archivePatterns = [
    /java\/util\/zip\/ZipInputStream/i,
    /java\/util\/zip\/ZipFile/i,
    /java\/util\/jar\/JarInputStream/i,
    /java\/util\/zip\/InflaterInputStream/i,
  ];
  const processPatterns = [
    /java\/lang\/Runtime\.exec/i,
    /java\/lang\/ProcessBuilder\.(?:<init>|start)/i,
    /ProcessBuilder\.start/i,
  ];
  const outboundBodyPatterns = [
    /HttpURLConnection\.getOutputStream/i,
    /BodyPublishers\.(?:ofString|ofByteArray|ofInputStream)/i,
    /RequestBody\.create/i,
    /FormBody\$Builder\.add/i,
    /OutputStream\.write/i,
  ];
  const launcherMaterialPatterns = [
    /launcher_accounts\.json/i,
    /launcher_profiles\.json/i,
    /accounts\.json/i,
    /\.minecraft[\\/](?:logs|launcher_accounts|launcher_profiles)/i,
  ];
  const screenshotPatterns = [
    /java\/awt\/Robot\.createScreenCapture/i,
    /\bcreateScreenCapture\b/i,
  ];
  const clipboardPatterns = [
    /Toolkit\.getSystemClipboard/i,
    /getSystemClipboard/i,
    /Clipboard\.getData/i,
  ];
  const keyCapturePatterns = [
    /KeyEventDispatcher/i,
    /KeyboardFocusManager\.addKeyEventDispatcher/i,
    /\bkeylogger\b/i,
  ];
  const defenderPatterns = [
    /Add-MpPreference\s+-ExclusionPath/i,
    /Set-MpPreference\s+-DisableRealtimeMonitoring/i,
    /\bsc(?:\.exe)?\s+(?:stop|config)\s+(?:WinDefend|Sense)\b/i,
    /\bnetsh\s+advfirewall\s+set\b/i,
    /DisableAntiSpyware/i,
  ];
  const scheduledTaskPatterns = [
    /\bschtasks(?:\.exe)?\b/i,
    /\bTaskScheduler\b/i,
    /\bNew-ScheduledTask\b/i,
  ];
  const registryStartupPatterns = [
    /Software[\\/]Microsoft[\\/]Windows[\\/]CurrentVersion[\\/]Run/i,
    /\\Start Menu\\Programs\\Startup/i,
    /\bWScript\.Shell\b/i,
    /\bUpdater\.vbs\b/i,
  ];
  const signatureEntryPatterns = [
    /META-INF\/CERTIFIC\.RSA/i,
    /META-INF\/CERTIFIC\.EC/i,
    /META-INF\/CERT\.SF/i,
    /META-INF\/CERTIFIC\.SF/i,
    /META-INF\/[^/\s]+\.(?:SF|RSA|DSA|EC)\b/i,
  ];
  const archiveRewritePatterns = [
    /ZipOutputStream/i,
    /JarOutputStream/i,
    /ZipEntry/i,
    /Files\.(?:delete|deleteIfExists|move|copy)/i,
  ];
  const antiAnalysisPatterns = [
    /\btasklist(?:\.exe)?\b/i,
    /\bVBox(?:Tray|Service|Guest)\b/i,
    /\bvmware\b/i,
    /\bvirtualbox\b/i,
    /\bwireshark(?:\.exe)?\b/i,
    /\btcpview(?:\.exe)?\b/i,
    /\bHTTPDebugger(?:UI|Svc)?\.exe\b/i,
  ];

  const webhookPatterns = [
    /discord(?:app)?\.com\/api\/webhooks\//i,
    /hooks\.slack\.com\/services\//i,
    /api\.telegram\.org\/bot/i,
    /(?:discord|slack|telegram)[^\s"']{0,40}webhook/i,
  ];
  const jndiPatterns = [
    /javax\/naming\/InitialContext/i,
    /\$\{jndi:/i,
    /ldap:\/\//i,
    /rmi:\/\//i,
    /Lookup\.lookup/i,
  ];
  const reflectiveLoadPatterns = [
    /java\/lang\/ClassLoader\.defineClass/i,
    /java\/security\/SecureClassLoader\.defineClass/i,
    /java\/net\/URLClassLoader/i,
    /Unsafe\.defineClass/i,
    /MethodHandles\.Lookup\.defineClass/i,
  ];
  const nativeLoadPatterns = [
    /System\.loadLibrary/i,
    /System\.load\(/i,
    /Runtime\.loadLibrary/i,
    /Native\.load/i,
  ];


  for (const classInfo of classes) {
    for (const method of classInfo.methods) {
      const methodLabel = `${method.name}${method.descriptor}`;
      const values = method.instructions.flatMap((instruction) => {
        const items: string[] = [];
        if (instruction.reference) items.push(instruction.reference);
        if (typeof instruction.literal === "string")
          items.push(instruction.literal);
        return items;
      });
      if (!values.length) continue;

      const accessesSession = matches(values, sessionPatterns);
      const readsUsername = matches(values, usernamePatterns);
      const readsUuid = matches(values, uuidPatterns);
      const readsToken = matches(values, tokenPatterns);
      const readsServer = matches(values, serverPatterns);
      const hardcodedPay = matches(values, payPatterns);
      const sendsChat = matches(values, sendChatPatterns);
      const network = matches(values, networkPatterns);
      const remoteRead = matches(values, remoteReadPatterns);
      const fileWrite = matches(values, fileWritePatterns);
      const process = matches(values, processPatterns);
      const outboundBody = matches(values, outboundBodyPatterns);

      if (accessesSession) {
        add({
          actionId: "minecraft-session-access",
          severity: "info",
          confidence: "high",
          category: "minecraft",
          title: "Accesses the active Minecraft session",
          description:
            "This method obtains the live Minecraft session object, which can expose account identity and authentication material.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, sessionPatterns),
          tags: ["minecraft", "session"],
        });
      }
      if (readsUsername) {
        add({
          actionId: "minecraft-username-read",
          severity: "info",
          confidence: "high",
          category: "minecraft",
          title: "Gets the Minecraft username",
          description:
            "This method reads the active account name from Minecraft session or profile APIs.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, usernamePatterns),
          tags: ["minecraft", "identity", "username"],
        });
      }
      if (readsUuid) {
        add({
          actionId: "minecraft-uuid-read",
          severity: "info",
          confidence: "high",
          category: "minecraft",
          title: "Gets the Minecraft UUID",
          description:
            "This method reads the active account UUID or profile identifier.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, uuidPatterns),
          tags: ["minecraft", "identity", "uuid"],
        });
      }
      if (readsToken) {
        add({
          actionId: "minecraft-token-read",
          severity: "high",
          confidence: "high",
          category: "credential",
          title: "Gets the Minecraft access token",
          description:
            "This method reads live Minecraft authentication material. Legitimate mods rarely need the access token.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, tokenPatterns),
          tags: ["minecraft", "credential", "access-token"],
        });
      }
      if (readsServer) {
        add({
          actionId: "minecraft-server-read",
          severity: "info",
          confidence: "high",
          category: "minecraft",
          title: "Gets the current Minecraft server",
          description:
            "This method reads the current multiplayer server address or connection metadata.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, serverPatterns),
          tags: ["minecraft", "server", "identity"],
        });
      }
      if (hardcodedPay) {
        add({
          actionId: sendsChat ? "minecraft-pay-send" : "minecraft-pay-literal",
          severity: sendsChat ? "high" : "medium",
          confidence: sendsChat ? "high" : "medium",
          category: "command",
          title: sendsChat
            ? "Sends a hardcoded /pay command"
            : "Contains a hardcoded /pay command",
          description: sendsChat
            ? "This method combines a /pay command string with a Minecraft chat or command-sending API, which can automate currency transfer."
            : "A /pay command is embedded in this method. Review its callers and how the recipient and amount are built.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, [...payPatterns, ...sendChatPatterns]),
          tags: ["minecraft", "command", "payment"],
        });
      }
      if (network) {
        add({
          actionId: "outbound-network-request",
          severity: "info",
          confidence: "high",
          category: "network",
          title: "Makes an outbound network request",
          description:
            "This method opens an HTTP, URL, or socket connection. Inspect its endpoints and transmitted data.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, networkPatterns),
          tags: ["network"],
        });
      }
      if (network && remoteRead) {
        add({
          actionId: "remote-content-download",
          severity: "medium",
          confidence: "high",
          category: "download",
          title: "Downloads remote content",
          description:
            "This method opens a network connection and consumes the response as bytes or a stream.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, [...networkPatterns, ...remoteReadPatterns]),
          tags: ["network", "download"],
        });
      }
      if (fileWrite) {
        add({
          actionId: "file-write",
          severity: "info",
          confidence: "high",
          category: "filesystem",
          title: "Writes a file to disk",
          description:
            "This method creates, copies, moves, or writes file content.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, fileWritePatterns),
          tags: ["filesystem", "write"],
        });
      }
      if (network && remoteRead && fileWrite) {
        add({
          actionId: "remote-download-to-disk",
          severity: "high",
          confidence: "high",
          category: "download",
          title: "Downloads a remote file to disk",
          description:
            "The same method combines network retrieval, response reading, and file writing, forming a concrete downloader path.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, [
            ...networkPatterns,
            ...remoteReadPatterns,
            ...fileWritePatterns,
          ]),
          tags: ["network", "download", "filesystem"],
        });
      }
      if (matches(values, archivePatterns)) {
        add({
          actionId: "archive-extraction",
          severity: "medium",
          confidence: "high",
          category: "filesystem",
          title: "Extracts an archive",
          description:
            "This method opens ZIP or JAR archive streams, which may unpack a bundled or downloaded second stage.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, archivePatterns),
          tags: ["archive", "extraction"],
        });
      }
      if (process) {
        add({
          actionId: "process-execution",
          severity: "high",
          confidence: "high",
          category: "execution",
          title: "Starts an operating-system process",
          description:
            "This method invokes Runtime.exec or ProcessBuilder to start a local command or executable.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, processPatterns),
          tags: ["process", "execution"],
        });
      }
      if (network && remoteRead && fileWrite && process) {
        add({
          actionId: "download-execute-chain",
          severity: "critical",
          confidence: "high",
          category: "execution",
          title: "Downloads and executes a payload",
          description:
            "One method retrieves remote bytes, writes them to disk, and starts a process. This is a complete download-and-execute chain.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, [
            ...networkPatterns,
            ...remoteReadPatterns,
            ...fileWritePatterns,
            ...processPatterns,
          ]),
          tags: ["downloader", "payload", "execution"],
        });
      }
      if ((readsToken || readsUsername || readsUuid) && network && outboundBody) {
        add({
          actionId: "minecraft-session-exfiltration",
          severity: "critical",
          confidence: "high",
          category: "credential",
          title: "Sends Minecraft session data outbound",
          description:
            "The same method reads Minecraft account or token data and builds an outbound request body, indicating session-data exfiltration.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, [
            ...usernamePatterns,
            ...uuidPatterns,
            ...tokenPatterns,
            ...networkPatterns,
            ...outboundBodyPatterns,
          ]),
          tags: ["minecraft", "credential", "exfiltration"],
        });
      }
      if (matches(values, launcherMaterialPatterns)) {
        add({
          actionId: "minecraft-account-file-read",
          severity: "high",
          confidence: "medium",
          category: "credential",
          title: "Targets Minecraft account or log files",
          description:
            "This method references launcher account, profile, or Minecraft log storage that can contain identity or session data.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, launcherMaterialPatterns),
          tags: ["minecraft", "credential", "filesystem"],
        });
      }
      if (matches(values, screenshotPatterns)) {
        add({
          actionId: "screen-capture",
          severity: "high",
          confidence: "high",
          category: "collection",
          title: "Captures the screen",
          description:
            "This method uses desktop APIs to capture screen pixels.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, screenshotPatterns),
          tags: ["surveillance", "screenshot"],
        });
      }
      if (matches(values, clipboardPatterns)) {
        add({
          actionId: "clipboard-read",
          severity: "high",
          confidence: "high",
          category: "collection",
          title: "Reads the system clipboard",
          description:
            "This method accesses clipboard contents, which may expose copied passwords, tokens, or wallet addresses.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, clipboardPatterns),
          tags: ["surveillance", "clipboard"],
        });
      }

      const hitsWebhook = matches(values, webhookPatterns);
      const hitsJndi = matches(values, jndiPatterns);
      const hitsReflectiveLoad = matches(values, reflectiveLoadPatterns);
      const hitsNativeLoad = matches(values, nativeLoadPatterns);

      if (hitsWebhook) {
        add({
          actionId: "webhook-exfiltration",
          severity: "high",
          confidence: "high",
          category: "network",
          title: "Contacts a webhook-style destination",
          description:
            "This method references Discord, Slack, Telegram, or generic webhook endpoints commonly used for credential and log exfiltration.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, webhookPatterns),
          tags: ["webhook", "exfiltration", "network"],
        });
      }
      if (hitsJndi) {
        add({
          actionId: "jndi-lookup",
          severity: "critical",
          confidence: "high",
          category: "execution",
          title: "Performs a JNDI or remote naming lookup",
          description:
            "JNDI/LDAP/RMI lookups can fetch and materialize remote objects. Treat this as high-risk dynamic code loading unless provenance is proven safe.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, jndiPatterns),
          tags: ["jndi", "ldap", "rmi", "dynamic-load"],
        });
      }
      if (hitsReflectiveLoad) {
        add({
          actionId: "reflective-class-load",
          severity: "high",
          confidence: "high",
          category: "execution",
          title: "Defines or loads classes reflectively",
          description:
            "Reflective ClassLoader.defineClass / URLClassLoader usage can execute bytes that never appear as ordinary .class entries in the archive.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, reflectiveLoadPatterns),
          tags: ["reflective", "classloader", "defineClass"],
        });
      }
      if (hitsNativeLoad) {
        add({
          actionId: "native-library-load",
          severity: "medium",
          confidence: "medium",
          category: "execution",
          title: "Loads a native library",
          description:
            "Native library loading can escape the JVM security model and run attacker-controlled machine code.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, nativeLoadPatterns),
          tags: ["native", "jni", "library"],
        });
      }

      if (matches(values, keyCapturePatterns)) {
        add({
          actionId: "keyboard-capture",
          severity: "high",
          confidence: "medium",
          category: "collection",
          title: "Captures keyboard input",
          description:
            "This method registers keyboard-event capture or contains an explicit keylogger marker.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, keyCapturePatterns),
          tags: ["surveillance", "keylogger"],
        });
      }
      const tampersWithDefender = matches(values, defenderPatterns);
      const createsScheduledTask = matches(values, scheduledTaskPatterns);
      const createsStartupEntry = matches(values, registryStartupPatterns);
      const rewritesArchive = matches(values, archiveRewritePatterns);
      const signatureEvidence = evidence(values, signatureEntryPatterns, 12);
      const antiAnalysis = matches(values, antiAnalysisPatterns);
      if (tampersWithDefender) {
        add({
          actionId: "security-product-tampering",
          severity: process ? "critical" : "high",
          confidence: process ? "high" : "medium",
          category: "evasion",
          title: "Weakens Microsoft Defender or firewall controls",
          description: process
            ? "This method passes a Defender or firewall weakening command into an operating-system process API."
            : "This method contains a Defender or firewall weakening command that should be traced to its executor.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, [
            ...defenderPatterns,
            ...processPatterns,
          ]),
          techniques: [ATTACK.disableOrModifyTools],
          tags: ["defender", "tampering", "security-control"],
        });
      }
      if (createsScheduledTask || createsStartupEntry) {
        const techniques = [
          ...(createsScheduledTask ? [ATTACK.scheduledTask] : []),
          ...(createsStartupEntry ? [ATTACK.registryRunKeys] : []),
        ];
        add({
          actionId: createsScheduledTask
            ? "scheduled-task-persistence"
            : "startup-persistence",
          severity: process || fileWrite ? "high" : "medium",
          confidence: process || fileWrite ? "high" : "medium",
          category: "persistence",
          title: createsScheduledTask
            ? "Creates scheduled-task persistence"
            : "Creates login or startup persistence",
          description:
            "The method combines an autostart mechanism with a command or payload path, enabling code to run again after login or reboot.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, [
            ...scheduledTaskPatterns,
            ...registryStartupPatterns,
            ...processPatterns,
            ...fileWritePatterns,
          ]),
          techniques,
          tags: ["persistence", "autostart"],
        });
      }
      if (
        tampersWithDefender &&
        (createsScheduledTask || createsStartupEntry) &&
        process
      ) {
        add({
          actionId: "defense-tampering-persistence-chain",
          severity: "critical",
          confidence: "high",
          category: "evasion",
          title: "Disables defenses and establishes persistence",
          description:
            "One ordered method launches security-tampering commands and creates an autostart mechanism. This is a concrete installation chain, not an archive-wide keyword correlation.",
          entry: classInfo.path,
          method: methodLabel,
          ordering: "proven",
          evidence: evidence(values, [
            ...defenderPatterns,
            ...scheduledTaskPatterns,
            ...registryStartupPatterns,
            ...processPatterns,
          ], 10),
          techniques: [
            ATTACK.disableOrModifyTools,
            ...(createsScheduledTask ? [ATTACK.scheduledTask] : []),
            ...(createsStartupEntry ? [ATTACK.registryRunKeys] : []),
          ],
          tags: ["installation-chain", "tampering", "persistence"],
        });
      }
      if (signatureEvidence.length >= 2 && rewritesArchive) {
        add({
          actionId: "jar-signature-stripping",
          severity: "critical",
          confidence: "high",
          category: "evasion",
          title: "Strips JAR signature metadata",
          description:
            "This method references multiple JAR signer records while rewriting or deleting archive entries. Removing these files lets modified JAR contents appear unsigned instead of visibly failing signature checks.",
          entry: classInfo.path,
          method: methodLabel,
          ordering: "proven",
          evidence: [
            ...signatureEvidence,
            ...evidence(values, archiveRewritePatterns, 4),
          ],
          techniques: [ATTACK.codeSigning, ATTACK.fileDeletion],
          tags: ["jar-infection", "signature-stripping", "supply-chain"],
        });
      }
      if (antiAnalysis && process) {
        add({
          actionId: "sandbox-process-discovery",
          severity: "high",
          confidence: "high",
          category: "evasion",
          title: "Checks for analysis and virtual-machine processes",
          description:
            "The method enumerates processes and checks for sandbox, virtual-machine, or packet-analysis tools before continuing.",
          entry: classInfo.path,
          method: methodLabel,
          evidence: evidence(values, [
            ...antiAnalysisPatterns,
            ...processPatterns,
          ]),
          techniques: [ATTACK.systemChecks, ATTACK.processDiscovery],
          tags: ["anti-analysis", "sandbox-evasion", "process-discovery"],
        });
      }
    }

    const classValues = [...classInfo.references, ...classInfo.strings];
    const hasEntryAction = (actionId: string) =>
      seenAtEntry.has(`${actionId}:${classInfo.path}`);
    const classNetworkPatterns = [
      /java[./]net[./]URL\.(?:openConnection|openStream)/i,
      /java[./]net[./]HttpURLConnection/i,
      /java[./]net[./]http[./]HttpClient\.(?:send|sendAsync)/i,
      /okhttp3[./](?:Call\.execute|OkHttpClient\.newCall)/i,
      /java[./]net[./]Socket\./i,
    ];
    const classRemoteReadPatterns = [
      /java[./]net[./]URL\.openStream/i,
      /InputStream\.(?:read|readAllBytes|transferTo)/i,
      /BodyHandlers\.ofByteArray/i,
      /ResponseBody\.(?:bytes|byteStream)/i,
    ];
    const classFileWritePatterns = [
      /java[./]nio[./]file[./]Files\.(?:write|writeString|copy|move)/i,
      /java[./]nio[./]file[./]Files\.newOutputStream/i,
      /FileOutputStream\.(?:<init>|write)/i,
      /FileWriter\.(?:<init>|write)/i,
      /RandomAccessFile\.(?:write|setLength)/i,
    ];
    const classProcessPatterns = [
      /java[./]lang[./]Runtime\.exec/i,
      /java[./]lang[./]ProcessBuilder\.(?:<init>|start)/i,
    ];
    const classNetwork = matches(classValues, classNetworkPatterns);
    const classRemoteRead = matches(classValues, classRemoteReadPatterns);
    const classFileWrite = matches(classValues, classFileWritePatterns);
    const classProcess = matches(classValues, classProcessPatterns);
    const classUsername = matches(classValues, usernamePatterns);
    const classUuid = matches(classValues, uuidPatterns);
    const classToken = matches(classValues, tokenPatterns);
    const classOutboundBody = matches(classValues, outboundBodyPatterns);

    for (const [
      actionId,
      matched,
      severity,
      category,
      title,
      description,
      patterns,
      tags,
    ] of [
      [
        "minecraft-username-read",
        classUsername,
        "info",
        "minecraft",
        "Gets the Minecraft username",
        "This class references APIs or fields that read the active Minecraft account name.",
        usernamePatterns,
        ["minecraft", "identity", "username"],
      ],
      [
        "minecraft-uuid-read",
        classUuid,
        "info",
        "minecraft",
        "Gets the Minecraft UUID",
        "This class references APIs or fields that read the active Minecraft UUID.",
        uuidPatterns,
        ["minecraft", "identity", "uuid"],
      ],
      [
        "minecraft-token-read",
        classToken,
        "high",
        "credential",
        "Gets the Minecraft access token",
        "This class references live Minecraft access-token APIs or fields.",
        tokenPatterns,
        ["minecraft", "credential", "access-token"],
      ],
    ] as const) {
      if (!matched || hasEntryAction(actionId)) continue;
      add({
        actionId,
        severity,
        confidence: "medium",
        category,
        title,
        description,
        entry: classInfo.path,
        evidence: evidence(classValues, patterns),
        tags: [...tags, "class-correlation"],
      });
    }

    if (
      classNetwork &&
      classRemoteRead &&
      classFileWrite &&
      !hasEntryAction("remote-download-to-disk")
    ) {
      add({
        actionId: "remote-download-to-disk",
        severity: "high",
        confidence: "medium",
        category: "download",
        title: "Downloads a remote file to disk",
        description:
          "Calls across this class combine network retrieval, response reading, and file writing, forming a likely downloader path.",
        entry: classInfo.path,
        evidence: evidence(classValues, [
          ...classNetworkPatterns,
          ...classRemoteReadPatterns,
          ...classFileWritePatterns,
        ]),
        tags: ["network", "download", "filesystem", "class-correlation"],
      });
    }
    if (
      classNetwork &&
      classRemoteRead &&
      classFileWrite &&
      classProcess &&
      !hasEntryAction("download-execute-chain")
    ) {
      add({
        actionId: "download-execute-chain",
        severity: "critical",
        confidence: "medium",
        category: "execution",
        title: "Downloads and executes a payload",
        description:
          "Calls across this class combine remote retrieval, file creation, and process execution. Review the call graph to confirm the complete chain.",
        entry: classInfo.path,
        evidence: evidence(classValues, [
          ...classNetworkPatterns,
          ...classRemoteReadPatterns,
          ...classFileWritePatterns,
          ...classProcessPatterns,
        ]),
        tags: ["downloader", "payload", "execution", "class-correlation"],
      });
    }
    if (
      (classUsername || classUuid || classToken) &&
      classNetwork &&
      classOutboundBody &&
      !hasEntryAction("minecraft-session-exfiltration")
    ) {
      add({
        actionId: "minecraft-session-exfiltration",
        severity: "critical",
        confidence: "medium",
        category: "credential",
        title: "Sends Minecraft session data outbound",
        description:
          "Calls across this class combine Minecraft identity or token access with outbound request-body creation.",
        entry: classInfo.path,
        evidence: evidence(classValues, [
          ...usernamePatterns,
          ...uuidPatterns,
          ...tokenPatterns,
          ...classNetworkPatterns,
          ...outboundBodyPatterns,
        ]),
        tags: ["minecraft", "credential", "exfiltration", "class-correlation"],
      });
    }
  }

  for (const context of contexts) {
    for (const value of context.values) {
      const decoded = value.match(
        /^\[decoded ([^@\]]+?)(?: @ ([^\]]+))?]\s*([\s\S]*)$/,
      );
      const clean = (decoded?.[3] ?? value).trim();
      const method = decoded?.[2]?.trim();
      const recovery = decoded?.[1]?.trim();
      if (/\bmcUsername\b/.test(clean) || (minecraftContext && /\bgetUsername\b/.test(clean))) {
        add({
          actionId: "minecraft-username-field",
          severity: "info",
          confidence: recovery ? "high" : "medium",
          category: "minecraft",
          title: "Builds a Minecraft username field",
          description:
            "The sample embeds or reconstructs a field used to collect or serialize the Minecraft account name.",
          entry: context.source,
          method,
          evidence: [recovery ? `${clean.slice(0, 190)} (${recovery})` : clean.slice(0, 220)],
          tags: ["minecraft", "identity", "username", "string-evidence"],
        });
      }
      if (
        /\bmcUuid\b/.test(clean) ||
        (minecraftContext && /\bgetUuid(?:OrNull)?\b/.test(clean))
      ) {
        add({
          actionId: "minecraft-uuid-field",
          severity: "info",
          confidence: recovery ? "high" : "medium",
          category: "minecraft",
          title: "Builds a Minecraft UUID field",
          description:
            "The sample embeds or reconstructs a field used to collect or serialize the Minecraft account UUID.",
          entry: context.source,
          method,
          evidence: [recovery ? `${clean.slice(0, 190)} (${recovery})` : clean.slice(0, 220)],
          tags: ["minecraft", "identity", "uuid", "string-evidence"],
        });
      }
      if (minecraftContext && /\baccessToken\b|\bgetAccessToken\b/.test(clean)) {
        add({
          actionId: "minecraft-token-field",
          severity: "high",
          confidence: recovery ? "high" : "medium",
          category: "credential",
          title: "Builds a Minecraft access-token field",
          description:
            "The sample embeds or reconstructs a field used to collect or serialize live Minecraft authentication material.",
          entry: context.source,
          method,
          evidence: [recovery ? `${clean.slice(0, 190)} (${recovery})` : clean.slice(0, 220)],
          tags: ["minecraft", "credential", "access-token", "string-evidence"],
        });
      }
      if (matches([clean], payPatterns)) {
        add({
          actionId: "minecraft-pay-literal",
          severity: "medium",
          confidence: "medium",
          category: "command",
          title: "Contains a hardcoded /pay command",
          description:
            "JarLab recovered a /pay command string. Review how its recipient and amount are constructed and whether it reaches a chat-sending API.",
          entry: context.source,
          method,
          evidence: [recovery ? `${clean} (${recovery})` : clean],
          tags: ["minecraft", "command", "payment", "recovered-string"],
        });
      }
      if (/\/shard\/submitMinecraftLog|submitMinecraftLog/i.test(clean)) {
        add({
          actionId: "minecraft-log-submit-route",
          severity: "high",
          confidence: "high",
          category: "credential",
          title: "Contains a Minecraft log exfiltration route",
          description:
            "The recovered route explicitly submits Minecraft logs or account telemetry to a remote service.",
          entry: context.source,
          method,
          evidence: [clean.slice(0, 220)],
          tags: ["minecraft", "logs", "exfiltration", "recovered-string"],
        });
      }
      if (
        /https?:\/\/\S+(?:\.jar|\.zip|\.exe|\.dll|\.py)(?:\b|[?#/])|\/(?:cdn|files|download|delivery)\/\S+/i.test(
          clean,
        )
      ) {
        add({
          actionId: "hardcoded-payload-location",
          severity: "medium",
          confidence: "medium",
          category: "download",
          title: "Contains a hardcoded payload location",
          description:
            "The sample embeds or reconstructs a route that resembles a downloadable archive, executable, script, or staged payload.",
          entry: context.source,
          method,
          evidence: [recovery ? `${clean.slice(0, 190)} (${recovery})` : clean.slice(0, 220)],
          tags: ["download", "payload", "recovered-string"],
        });
      }
      if (
        /\b(?:curl|wget)\b[^\r\n]{0,180}https?:\/\/|\bInvoke-WebRequest\b|\bDownloadFile\s*\(|\bStart-BitsTransfer\b/i.test(
          clean,
        )
      ) {
        add({
          actionId: "hardcoded-download-command",
          severity: "high",
          confidence: "high",
          category: "download",
          title: "Runs a hardcoded download command",
          description:
            "The sample embeds or reconstructs a shell command that downloads remote content.",
          entry: context.source,
          method,
          evidence: [recovery ? `${clean.slice(0, 190)} (${recovery})` : clean.slice(0, 220)],
          tags: ["download", "command", "payload", "recovered-string"],
        });
      }
    }
  }

  const reachable = new Set(analysis.reachableMethodKeys ?? []);
  const flows = analysis.flows ?? [];
  const classByPath = new Map(classes.map((item) => [item.path, item]));
  const uniqueTechniques = (items: AttackTechnique[]) => {
    const ids = new Set<string>();
    return items.filter((item) => {
      if (ids.has(item.id)) return false;
      ids.add(item.id);
      return true;
    });
  };
  const mappedActions = actions.map((action) => {
    const classInfo = classByPath.get(action.entry);
    const methodName = action.method?.match(/^([^<(]+)/)?.[1];
    const reachabilityKey =
      classInfo && methodName ? `${classInfo.name}#${methodName}` : undefined;
    const relatedFlows = flows.filter(
      (flow) =>
        flow.entry === action.entry &&
        (!methodName || flow.method === methodName),
    );
    const actionTechniques: AttackTechnique[] = [
      ...(action.techniques ?? []),
      ...relatedFlows.flatMap((flow) => flow.techniques),
    ];
    if (
      [
        "minecraft-token-read",
        "minecraft-token-field",
        "minecraft-session-exfiltration",
      ].includes(action.actionId)
    ) {
      actionTechniques.push(ATTACK.applicationToken);
    }
    if (action.actionId === "minecraft-account-file-read")
      actionTechniques.push(ATTACK.unsecuredCredentials);
    if (action.actionId === "screen-capture")
      actionTechniques.push(ATTACK.screenCapture);
    if (action.actionId === "clipboard-read")
      actionTechniques.push(ATTACK.clipboard);

    if (action.actionId === "webhook-exfiltration")
      actionTechniques.push(ATTACK.exfiltration);
    if (action.actionId === "jndi-lookup" || action.actionId === "reflective-class-load")
      actionTechniques.push(ATTACK.ingressTransfer);
    if (action.actionId === "native-library-load")
      actionTechniques.push(ATTACK.commandShell);
    if (action.actionId === "keyboard-capture")
      actionTechniques.push(ATTACK.inputCapture);
    if (
      [
        "remote-content-download",
        "remote-download-to-disk",
        "download-execute-chain",
      ].includes(action.actionId)
    ) {
      actionTechniques.push(ATTACK.ingressTransfer);
    }
    if (action.actionId === "minecraft-session-exfiltration")
      actionTechniques.push(ATTACK.exfiltration);
    const destinations = Array.from(
      new Set([
        ...(action.destinations ?? []),
        ...relatedFlows.flatMap((flow) => flow.destinations),
        ...destinationsFromValues(action.evidence),
      ]),
    ).slice(0, 8);
    const dataTypes = Array.from(
      new Set([
        ...(action.dataTypes ?? []),
        ...relatedFlows.flatMap((flow) => flow.dataTypes),
      ]),
    );
    const isReachable =
      relatedFlows.some((flow) => flow.reachable) ||
      (reachabilityKey ? reachable.has(reachabilityKey) : false);
    return {
      ...action,
      reachable: reachabilityKey ? isReachable : action.reachable,
      entryPoint:
        action.entryPoint ??
        (reachabilityKey
          ? analysis.entryPointLabels?.[reachabilityKey]
          : undefined),
      ordering:
        action.ordering ??
        (relatedFlows.some((flow) => flow.ordering === "proven")
          ? "proven"
          : relatedFlows.length
            ? "partial"
            : "correlated"),
      dataTypes,
      destinations,
      techniques: uniqueTechniques(actionTechniques),
      relatedFlowIds: relatedFlows.map((flow) => flow.id),
    };
  });

  return mappedActions.sort(
    (left, right) =>
      severityOrder[left.severity] - severityOrder[right.severity] ||
      left.title.localeCompare(right.title) ||
      left.entry.localeCompare(right.entry),
  );
}
